// Единый клиент Google Sheets для Splav.
// Стратегия: gviz (быстрый, прямой) -> Apps Script (медленный, но обходит RU блокировки).
// Apps Script запросы идут ~3-4с (cold-start). Gviz — обычно <500мс.

const APPS_SCRIPT_URL =
  'https://script.google.com/macros/s/AKfycbxZ-KFwBXnCaxHCMz55BTYIJQbGmUxLn_2IfbiQRY0HMoVBqTbyFNknGjj8QUxmWaDarQ/exec';

const SHEET_ID = '1DySmYtEDwh-fLYxDlIfF3PqTZRp4IQDDoWLx-Cp4g48';

// localStorage TTL: 7 дней
const LS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const _qs = (params) =>
  Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

// --- Direct gviz fetch (быстрый путь) ---
async function _gvizFetch(sheetName, signal, opts) {
  opts = opts || {};
  const params = [`tqx=out:json`, `sheet=${encodeURIComponent(sheetName)}`];
  if (opts.range) params.push(`range=${encodeURIComponent(opts.range)}`);
  if (opts.tq) params.push(`tq=${encodeURIComponent(opts.tq)}`);
  const url = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?${params.join('&')}`;
  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const text = await resp.text();
  // gviz обёртывает ответ в google.visualization.Query.setResponse(...)
  const js = text.substring(text.indexOf('{'), text.lastIndexOf('}') + 1);
  const data = JSON.parse(js);
  if (!data.table || !Array.isArray(data.table.rows)) {
    throw new Error('gviz: invalid response');
  }
  // Преобразуем в массив массивов (как у Apps Script)
  const cols = (data.table.cols || []).map((c) => c.label || '');
  const rows = [cols];
  for (const r of data.table.rows) {
    const row = (r.c || []).map((c) => (c ? (c.v != null ? c.v : '') : ''));
    rows.push(row);
  }
  return rows;
}

// --- Apps Script fetch (надёжный fallback) ---
async function _appsScriptFetch(params, signal) {
  const url = `${APPS_SCRIPT_URL}?${_qs(params)}`;
  const resp = await fetch(url, { signal });
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.json();
}

// --- Главный fetch с race + fallback ---
// Сначала запускает gviz и Apps Script параллельно, возвращает кто первый ответит.
// Если gviz упал (CORS, блок ISP), всё равно дождётся Apps Script.
async function _fetchSheet(sheetName) {
  const ctrlGviz = new AbortController();
  const ctrlAS = new AbortController();

  const gvizPromise = _gvizFetch(sheetName, ctrlGviz.signal);
  const asPromise = _appsScriptFetch({ sheet: sheetName }, ctrlAS.signal)
    .then((j) => (j && Array.isArray(j.data) ? j.data : Promise.reject(new Error('AS: bad'))));

  // Первый успешный — победил, второго отменяем.
  return new Promise((resolve, reject) => {
    let pending = 2;
    let lastErr = null;
    const onSuccess = (rows, otherCtrl) => {
      try { otherCtrl.abort(); } catch {}
      resolve(rows);
    };
    const onFail = (err) => {
      lastErr = err;
      pending -= 1;
      if (pending === 0) reject(lastErr || new Error('all failed'));
    };
    gvizPromise.then((rows) => onSuccess(rows, ctrlAS), onFail);
    asPromise.then((rows) => onSuccess(rows, ctrlGviz), onFail);
  });
}

// --- localStorage helpers ---
function _lsRead(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj.t !== 'number') return null;
    if (Date.now() - obj.t > LS_TTL_MS) return null;
    return obj.v;
  } catch { return null; }
}

function _lsWrite(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify({ t: Date.now(), v: value }));
  } catch {}
}

// --- Public API ---

// Кеш в памяти — после первого fetch одного и того же листа все последующие
// чтения возвращаются мгновенно (важно для fetchSlideHtml в Pitch.jsx,
// чтобы не дёргать AS повторно).
const _memoryCache = new Map();
const _inflight = new Map();

// Полные строки листа (legacy + быстрый gviz путь)
export async function fetchSheetRows(sheetName) {
  if (_memoryCache.has(sheetName)) return _memoryCache.get(sheetName);
  if (_inflight.has(sheetName)) return _inflight.get(sheetName);

  const p = (async () => {
    try {
      const rows = await _fetchSheet(sheetName);
      _memoryCache.set(sheetName, rows);
      return rows;
    } catch {
      return [];
    } finally {
      _inflight.delete(sheetName);
    }
  })();
  _inflight.set(sheetName, p);
  return p;
}

// Auth — читаем лист, проверяем на клиенте (как было раньше).
export async function authUser(sheetName, user, pass) {
  const rows = await fetchSheetRows(sheetName);
  for (let i = 1; i < rows.length; i++) {
    const r = rows[i] || [];
    if (String(r[1] || '') === user && String(r[2] || '') === pass) {
      return { ok: true, name: String(r[0] || '') };
    }
  }
  return { ok: false };
}

// Pitch meta — только колонки A,B,D,E (без тяжёлого HTML).
// gviz отвечает за ~200-500мс. Apps Script — fallback.
export async function fetchPitchMeta() {
  // Сначала пробуем быстрый gviz с select
  try {
    const ctrl = new AbortController();
    const rows = await _gvizFetch('PITCH', ctrl.signal, { tq: 'select A,B,D,E' });
    // Возвращаем в формате [Title, Desc, '', Level, Bg]
    const normalized = [['Title', 'Desc', 'HTML', 'Level', 'Bg']];
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i] || [];
      normalized.push([r[0] || '', r[1] || '', '', r[2] || '', r[3] || '']);
    }
    return { rows: normalized, prefilledHtml: {} };
  } catch {}

  // Fallback — Apps Script полный
  try {
    const json = await _appsScriptFetch({ sheet: 'PITCH' });
    const rows = (json && Array.isArray(json.data)) ? json.data : [];
    const prefilledHtml = {};
    for (let i = 1; i < rows.length; i++) {
      const r = rows[i] || [];
      if (r[2]) prefilledHtml[i + 1] = String(r[2]);
    }
    return { rows, prefilledHtml };
  } catch {
    return { rows: [], prefilledHtml: {} };
  }
}

export function fetchPitchMetaSWR() {
  const cached = _lsRead('splav:pitch_meta');
  const fresh = fetchPitchMeta().then((res) => {
    if (res && Array.isArray(res.rows) && res.rows.length > 0) {
      _lsWrite('splav:pitch_meta', res);
    }
    return res;
  });
  return { cached, fresh };
}

// HTML одного слайда через gviz range=C{row} (~200мс) с in-memory кешем.
const _slideHtmlCache = new Map();
const _slideHtmlInflight = new Map();

export async function fetchSlideHtml(row) {
  if (_slideHtmlCache.has(row)) return _slideHtmlCache.get(row);
  if (_slideHtmlInflight.has(row)) return _slideHtmlInflight.get(row);

  const p = (async () => {
    try {
      const ctrl = new AbortController();
      const rows = await _gvizFetch('PITCH', ctrl.signal, { range: `C${row}` });
      // [headers, [value]]
      const html = (rows[1] && rows[1][0] != null) ? String(rows[1][0]) : '';
      _slideHtmlCache.set(row, html);
      return html;
    } catch {
      // Fallback: грузим весь PITCH через Apps Script
      try {
        const json = await _appsScriptFetch({ sheet: 'PITCH' });
        const rows = (json && Array.isArray(json.data)) ? json.data : [];
        const r = rows[row - 1] || [];
        const html = String(r[2] || '');
        _slideHtmlCache.set(row, html);
        return html;
      } catch {
        return '';
      }
    } finally {
      _slideHtmlInflight.delete(row);
    }
  })();
  _slideHtmlInflight.set(row, p);
  return p;
}

// Single cell — через gviz range, ~200мс.
export async function fetchCell(sheetName, cell) {
  try {
    const ctrl = new AbortController();
    const rows = await _gvizFetch(sheetName, ctrl.signal, { range: cell });
    if (rows[1] && rows[1][0] != null) return String(rows[1][0]);
  } catch {}
  // Fallback — полный лист
  try {
    const rows = await fetchSheetRows(sheetName);
    const m = /^([A-Z]+)(\d+)$/.exec(cell);
    if (!m) return '';
    const col = m[1].split('').reduce((a, ch) => a * 26 + (ch.charCodeAt(0) - 64), 0) - 1;
    const r = parseInt(m[2], 10) - 1;
    return rows[r] && rows[r][col] != null ? String(rows[r][col]) : '';
  } catch {
    return '';
  }
}

export function fetchCellSWR(sheetName, cell) {
  const key = `splav:cell:${sheetName}:${cell}`;
  const cached = _lsRead(key);
  const fresh = fetchCell(sheetName, cell).then((value) => {
    if (value && value.trim()) _lsWrite(key, value);
    return value;
  });
  return { cached, fresh };
}

// One-pager content (ONE-PAGER!A1)
export function fetchOnePagerSWR() {
  return fetchCellSWR('ONE-PAGER', 'A1');
}

// --- Telegram notifications (отправка напрямую в Bot API) ---
const TG_TOKEN = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
const TG_CHAT_ID = process.env.REACT_APP_TELEGRAM_CHAT_ID;

async function _sendTelegram(text) {
  if (!TG_TOKEN || !TG_CHAT_ID) return { ok: false };
  try {
    const resp = await fetch(`https://api.telegram.org/bot${TG_TOKEN}/sendMessage`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        chat_id: TG_CHAT_ID,
        text,
        parse_mode: 'HTML',
        disable_web_page_preview: true,
      }),
    });
    return { ok: resp.ok };
  } catch {
    return { ok: false };
  }
}

function _esc(v) {
  return String(v == null ? '' : v)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function _nowMSK() {
  const d = new Date();
  const opt = { timeZone: 'Europe/Moscow', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' };
  return new Intl.DateTimeFormat('ru-RU', opt).format(d).replace(',', '');
}

// Лог входа (legacy) — пишем через Apps Script (POST), не блокирует UI.
export function logLogin({ username, ip, device }) {
  try {
    fetch(APPS_SCRIPT_URL, {
      method: 'POST',
      mode: 'no-cors',
      body: JSON.stringify({ type: 'login_log', username, ip, device }),
    }).catch(() => {});
  } catch {}
}

// Заявка на вход для инвестора → Telegram.
export async function sendInvestorRequest({ name, phone, email }) {
  const msg =
    `<b>Новая заявка: ИНВЕСТОР</b>\n\n` +
    `<b>Имя:</b> ${_esc(name)}\n` +
    `<b>Телефон:</b> ${_esc(phone)}\n` +
    `<b>Email:</b> ${_esc(email)}\n\n` +
    `<i>${_nowMSK()} MSK</i>`;
  return _sendTelegram(msg);
}

// Заявка на вакансию → Telegram.
export async function sendVacancyRequest({ vacancy, name, email, telegram, resume }) {
  const msg =
    `<b>Новая заявка: ВАКАНСИЯ</b>\n` +
    `<b>${_esc(vacancy)}</b>\n\n` +
    `<b>Имя:</b> ${_esc(name)}\n` +
    `<b>Email:</b> ${_esc(email)}\n` +
    `<b>Telegram:</b> ${_esc(telegram)}\n` +
    `<b>Резюме:</b> ${_esc(resume)}\n\n` +
    `<i>${_nowMSK()} MSK</i>`;
  return _sendTelegram(msg);
}
